import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Space-terms effective rent. Formula matches BOTH production stacks — the live FE form
 * (`auto-calculation-space-terms-section.ts:300`) and bulkedit (`space-calculations.js:182`):
 *
 *   effectiveRent = ((leaseTerm − rentFree) / leaseTerm) × achievableRent
 *
 * i.e. achievable rent pro-rated for the worked (non-rent-free) portion of the term.
 *
 * NOTE (corrected 2026-06-10): an earlier port used `(leaseTerm − rentFree) / (leaseTerm × achievableRent)`
 * copied from the now-dead `space-terms-calc-helper.ts`. That DIVERGED from both production stacks
 * (off by ~achievableRent²) and was wrong; this is the canonical formula both stacks compute.
 *
 * Returns 0 when achievable/term is missing or non-positive (div-by-zero guard). Callers own the
 * `rentFree ≥ term → 0` and `result ≤ 0 → null` clamps (they differ per stack: FE → 0, bulkedit → null).
 * The host rounds to 2 decimals; here that's the injected `round` / {@link defaultRound}.
 *
 * KERNEL ONLY — {@link effectiveRentRule} no longer routes through this function: the live binder
 * computes Formula A inline with different guard semantics (see the rule below). The kernel stays
 * exported for bulkedit parity (its caller owns the bulkedit-specific clamps).
 */
export function calculateEffectiveRent(achievableRent, leaseTerm, rentFree = 0) {
  if (!achievableRent || !leaseTerm || leaseTerm <= 0 || achievableRent <= 0) {
    return 0;
  }
  return (leaseTerm - rentFree) / leaseTerm * achievableRent;
}
/**
 * Effective rent from achievable rent, lease term and rent-free period — guard-for-guard match of
 * the LIVE binder ("Formula A" in `projects/miq/auto-calculation/src/lib/listeners/
 * space-terms.binder.ts` `calculateEffectiveRent`, ~line 261), which is what runs in prod.
 *
 * v2.0.0 guard reconciliation (S4) — deltas vs v1.0.0 (which used the kernel + a no-op guard):
 *  1. Missing inputs now CLEAR the output (write null) instead of no-op'ing (v1 left stale values).
 *  2. rentFree is REQUIRED: null/undefined rentFree → null (v1 defaulted missing rentFree to 0 and
 *     computed). rentFree = 0 is still valid and computes the full achievable rent.
 *  3. achievableRent === 0 → null (v1 wrote 0); leaseTerm === 0 → null (v1 no-op'd).
 *  4. rentFree ≥ leaseTerm → writes 0 (v1 had no clamp and wrote the negative product).
 *  5. NEGATIVE leaseTerm is NOT rejected (binder only checks `!== 0`): rentFree ≥ 0 > term makes
 *     clamp 4 fire, so a negative term writes 0 (v1 no-op'd on term ≤ 0). Binder quirk, pinned.
 *  6. Non-finite result (NaN/±Infinity from junk inputs) → null.
 * Host-owned, NOT modelled: the UOM auto-select on write / UOM clear on null, dirty/touched gating,
 * and the chained total-monthly-outgoing re-trigger.
 */
export const effectiveRentRule = {
  id: 'space.effective-rent.from-achievable-term-rentfree',
  inputs: [FieldKey.SpaceAchievableRent, FieldKey.SpaceLeaseTerm, FieldKey.SpaceRentFree],
  outputs: [FieldKey.SpaceEffectiveRent],
  formulaVersion: '2.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const achievableRent = ctx.get(FieldKey.SpaceAchievableRent);
    const leaseTerm = ctx.get(FieldKey.SpaceLeaseTerm);
    const rentFree = ctx.get(FieldKey.SpaceRentFree);
    // Binder `hasAllRequiredValues`: achievable/term present and !== 0, rentFree merely present.
    if (achievableRent == null || achievableRent === 0 || leaseTerm == null || leaseTerm === 0 || rentFree == null) {
      ctx.set(FieldKey.SpaceEffectiveRent, null);
      return;
    }
    // Binder: `rentFree || 0` (falsy '' / NaN collapse to 0 — NOT `?? 0`).
    const rentFreeValue = Number(rentFree) || 0;
    const leaseTermValue = Number(leaseTerm);
    if (rentFreeValue >= leaseTermValue) {
      ctx.set(FieldKey.SpaceEffectiveRent, 0);
      return;
    }
    const effectiveRentValue = (leaseTermValue - rentFreeValue) / leaseTermValue * Number(achievableRent);
    if (!Number.isFinite(effectiveRentValue)) {
      ctx.set(FieldKey.SpaceEffectiveRent, null);
      return;
    }
    ctx.set(FieldKey.SpaceEffectiveRent, round(effectiveRentValue, FieldKey.SpaceEffectiveRent));
  }
};
